// JOBE-0098.2: operator vocabulary and a small token/op splitter.
//
// Longest match wins (`>=` / `<=` / `!=` before `>` / `<` / `:`).

import { FRIENDLY_VOCABULARY } from './vocabulary.js';

export const OPERATORS = Object.freeze([':', '>=', '<=', '!=', '>', '<']);

const OPERATORS_BY_LENGTH = Object.freeze(
  [...OPERATORS].sort((a, b) => b.length - a.length),
);

// stripOpFromWire: vocabulary entries bake the canonical op into
// `wirePredicate` (e.g. `since → committedAt>=`). The parser emits
// `op` separately, so the rawPredicate field always wants the bare
// predicate name. This is the single source-of-truth normalization
// from the vocabulary's wire spelling to the parser's wire shape.
export function stripOpFromWire(wire) {
  if (!wire) return wire;
  return wire.replace(/(>=|<=|!=|>|<)$/, '');
}

// DATE_TOKENS: friendly date tokens that resolve to a bare wire-side
// date predicate. Derived from the vocabulary's date entries so the
// vocabulary remains the single source of truth — `since`, `until`,
// and `created` are read from FRIENDLY_VOCABULARY and stripped via
// `stripOpFromWire`. `updated` is parser-only (not in the picker
// vocabulary) and is listed explicitly: it's the canonical
// user-facing token for an arbitrary-operator `committedAt` filter.
const DATE_FRIENDLY_TOKENS = ['since', 'until', 'created'];
export const DATE_TOKENS = Object.freeze(
  DATE_FRIENDLY_TOKENS.reduce(
    (acc, friendly) => {
      const entry = FRIENDLY_VOCABULARY.find(
        (v) => v.friendlyToken === friendly,
      );
      if (entry) acc[friendly] = stripOpFromWire(entry.wirePredicate);
      return acc;
    },
    { updated: 'committedAt' },
  ),
);

// splitTokenOp walks a chunk like `updated>=2026-04-01` and returns
// { token: 'updated', op: '>=', rest: '2026-04-01' } — longest op wins.
// If no operator is found, returns { token: chunk, op: null, rest: '' }.
export function splitTokenOp(chunk) {
  if (!chunk) return { token: '', op: null, rest: '' };
  let earliest = -1;
  let found = null;
  // OPERATORS_BY_LENGTH is sorted longest-first, so at a tied position
  // the longer operator is visited first and wins via the `idx < earliest`
  // guard on subsequent shorter operators.
  for (const op of OPERATORS_BY_LENGTH) {
    const idx = chunk.indexOf(op);
    if (idx === -1) continue;
    if (earliest === -1 || idx < earliest) {
      earliest = idx;
      found = op;
    }
  }
  if (found === null || earliest === 0) {
    return { token: chunk, op: null, rest: '' };
  }
  return {
    token: chunk.slice(0, earliest),
    op: found,
    rest: chunk.slice(earliest + found.length),
  };
}
